import {
    DownOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    SwapOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Dropdown, Space, Tag, Tooltip } from 'antd';
import Table, { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface';
import { Profile } from 'oidc-client';
import React from 'react';
import { Link } from 'react-router-dom';
import authService from '../../../components/api-authorization/AuthorizeService';
import AuthenticatedAttachmentImage, {
    ImagePlaceholder,
} from '../../../components/authenticated-attachment-image';
import { defaultTablePagination, DRAWER_WIDTH } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { ActionType, ModuleName } from '../../../core/models/enum';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import { downloadFile } from '../../../helpers/FileHelper';
import { getSearchFilter } from '../../../helpers/FilterHelper';
import { showError } from '../../../helpers/NotificationHelper';
import { getTableLocale } from '../../../helpers/TableHelper';
import {
    RoleEnum,
    ToolTransfersClient,
    ToolVm,
    ToolsClient,
    TransferEmployeeVm,
    TransferWarehouseVm,
} from '../../../utils/api';
import { getSiteManagerProjects, locationKey, parseLocationKey } from '../../transfers/helpers';
import LocationSelect from '../../transfers/location-select/LocationSelect';
import TransferForm from '../../transfers/transfer-form/TransferForm';
import CertificateValidity from '../certificate-validity/CertificateValidity';
import { Props } from './index';
import {
    applySearchParams,
    getFilterParamsString,
    getFiltersParams,
    getPagingSortingParams,
} from '../../../helpers/SearchParamsHelper';

/** List thumbnail size: big enough to recognise a tool at a glance (the server thumbnail is 320 px). */
const LIST_IMAGE_PX = 80;

/** Newest tools first unless the user sorts by something else. */
const DEFAULT_SORT_BY = 'number';
const DEFAULT_SORT_ORDER: SortOrder = 'descend';

/** Where a tool is: its warehouse or the person holding it, as a location key. */
const toolLocationKey = (tool: ToolVm): string | undefined =>
    locationKey(tool.currentWarehouseId, tool.currentEmployeeId);

interface State {
    tools?: ToolVm[];
    warehouses?: TransferWarehouseVm[];
    employees?: TransferEmployeeVm[];
    loading?: boolean;
    exporting?: boolean;
    totalItems?: number;
    /** Rows, not just keys: the selection survives paging, and the source is read off the rows. */
    selectedTools: ToolVm[];
    /** Site manager's projects; null for roles that may move tools from any location. */
    ownProjects: string[] | null;
    /** Tools and source for the open transfer drawer: the selection, or a single row's tool. */
    transferPreset?: { fromKey: string; toolIds: number[] };
    allowCreateTransfer: boolean;
}

class ToolTable extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            tools: [],
            warehouses: [],
            employees: [],
            totalItems: 0,
            selectedTools: [],
            ownProjects: null,
            allowCreateTransfer: false,
        };
    }

    public componentDidMount = () => {
        this.getTools();
        this.getLocations();
        void this.getPermissions();
    };

    public componentDidUpdate = (prevProps: Props) => {
        const { location } = this.props;

        if (prevProps.location.search !== location.search) {
            const tab = (search: string) => new URLSearchParams(search).get('activeTab');

            // A selection made on one tab must not carry over to the other (archived tools
            // cannot go on a transfer).
            if (tab(prevProps.location.search) !== tab(location.search)) {
                this.setState({ selectedTools: [] });
            }

            this.getTools();
        }
    };

    private getPermissions = async () => {
        const userProfile: Profile = await authService.getUserProfile();

        const isSiteManager = parseInt(userProfile['userRoleId']) === RoleEnum.SiteManager;

        this.setState({
            ownProjects: isSiteManager ? getSiteManagerProjects(userProfile) : null,
            allowCreateTransfer: authorizeAction(
                userProfile,
                ModuleName.Transfers,
                ActionType.Create
            ),
        });
    };

    private getLocations = async () => {
        const client = new ToolTransfersClient();
        const [warehouses, employees] = await Promise.all([
            client.getWarehouses(),
            client.getEmployees(),
        ]);

        this.setState({ warehouses, employees });
    };

    /** Selected location filter from the URL. */
    private getLocationParams = () => {
        const params = new URLSearchParams(this.props.location.search);
        const warehouseId = parseInt(params.get('warehouseId') || '');
        const employeeId = parseInt(params.get('employeeId') || '');

        return {
            warehouseId: isNaN(warehouseId) ? undefined : warehouseId,
            employeeId: isNaN(employeeId) ? undefined : employeeId,
        };
    };

    private handleTableChange = async (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolVm> | SorterResult<ToolVm>[] = {}
    ) => {
        const {
            history,
            location: { search },
        } = this.props;

        const currSearchParams = new URLSearchParams(search);
        const tableSearchParams = this.createTableSearchParams(pagination, filters, sorter);
        const newSearchParams = applySearchParams(currSearchParams, tableSearchParams);

        history.push({
            pathname: history.location.pathname,
            search: newSearchParams.toString(),
        });
    };

    private createTableSearchParams = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolVm> | SorterResult<ToolVm>[] = {}
    ): URLSearchParams => {
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const filterParamsString = getFilterParamsString(filters);
        const singleSorter = sorter as SorterResult<ToolVm>;
        const tableSearchParams = new URLSearchParams(filterParamsString);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';
        const { warehouseId, employeeId } = this.getLocationParams();

        tableSearchParams.append('pageIndex', pagination.current?.toString() || '1');
        tableSearchParams.append(
            'pageSize',
            pagination.pageSize?.toString() || pagination.defaultPageSize?.toString() || ''
        );
        tableSearchParams.append(
            'sortBy',
            singleSorter.field?.toString() && singleSorter.order
                ? singleSorter.field?.toString()
                : ''
        );
        tableSearchParams.append('sortOrder', singleSorter.order || '');
        tableSearchParams.append('activeTab', activeTab ? activeTab : 'active');

        if (warehouseId) {
            tableSearchParams.append('warehouseId', warehouseId.toString());
        }

        if (employeeId) {
            tableSearchParams.append('employeeId', employeeId.toString());
        }

        return tableSearchParams;
    };

    private handleLocationChange = (key?: string) => {
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);
        const { warehouseId, employeeId } = parseLocationKey(key);

        params.delete('warehouseId');
        params.delete('employeeId');

        if (warehouseId) params.set('warehouseId', warehouseId.toString());
        if (employeeId) params.set('employeeId', employeeId.toString());

        params.set('pageIndex', '1');

        this.setState({ selectedTools: [] });

        history.push({
            pathname: history.location.pathname,
            search: params.toString(),
        });
    };

    /** Sort from the URL, falling back to newest first. */
    private getSort = (): { sortBy: string; sortOrder: SortOrder } => {
        const { sortByParam, sortOrderParam } = getPagingSortingParams(
            new URLSearchParams(this.props.location.search)
        );

        return sortByParam && sortOrderParam
            ? { sortBy: sortByParam, sortOrder: sortOrderParam as SortOrder }
            : { sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER };
    };

    public getTools = async () => {
        this.setState({
            loading: true,
        });

        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';
        const { warehouseId, employeeId } = this.getLocationParams();
        const { pageIndexParam, pageSizeParam } = getPagingSortingParams(params);
        const { sortBy, sortOrder } = this.getSort();
        const filters = getFiltersParams(params);

        try {
            const result = await new ToolsClient().getPaginated(
                filters.name ? filters.name.toString() : undefined,
                filters.description ? filters.description.toString() : undefined,
                activeTab === 'archived',
                warehouseId,
                employeeId,
                this.getNumberFilter(filters),
                pageIndexParam && parseInt(pageIndexParam) ? parseInt(pageIndexParam) : 1,
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
                sortBy,
                sortOrder || undefined
            );

            this.setState({
                tools: result.pageItems,
                loading: false,
                totalItems: result.totalItems,
            });
        } catch {
            this.setState({ loading: false });
            showError(translations.general.errorLoadingData);
        }
    };

    /** Number search: only a whole number filters; anything else is ignored. */
    private getNumberFilter = (filters: Record<string, unknown>): number | undefined => {
        const value = parseInt((filters.number || '').toString().trim());

        return isNaN(value) ? undefined : value;
    };

    /** PDF or Excel of exactly what the list shows: same tab, location and search filters. */
    private handleExport = async (format: 'pdf' | 'xlsx') => {
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const filters = getFiltersParams(params);
        const { warehouseId, employeeId } = this.getLocationParams();
        const client = new ToolsClient();
        const exportFile = format === 'pdf' ? client.exportPdf : client.exportXlsx;

        this.setState({ exporting: true });

        try {
            downloadFile(
                await exportFile.call(
                    client,
                    warehouseId,
                    employeeId,
                    filters.name ? filters.name.toString() : undefined,
                    filters.description ? filters.description.toString() : undefined,
                    this.getNumberFilter(filters),
                    params.get('activeTab') === 'archived'
                )
            );
        } catch {
            showError(
                format === 'pdf'
                    ? translations.tools.exportPdfError
                    : translations.tools.exportXlsxError
            );
        } finally {
            this.setState({ exporting: false });
        }
    };

    /** Why a tool cannot go on a transfer from this list, or undefined when it can. */
    private notSelectableReason = (
        record: ToolVm,
        ownWarehouseIds: number[]
    ): string | undefined => {
        const { ownProjects } = this.state;

        if (record.isArchived) return translations.transfers.notSelectableArchived;
        if (!toolLocationKey(record)) return translations.transfers.notSelectableNoWarehouse;
        if (record.isOnActiveTransfer) return translations.transfers.onActiveTransfer;

        // A site manager moves tools out of his own warehouses only - not from a person.
        if (
            ownProjects != null &&
            (record.currentWarehouseId == null ||
                !ownWarehouseIds.includes(record.currentWarehouseId))
        ) {
            return translations.transfers.notSelectableNotOwn;
        }

        return undefined;
    };

    /** A transfer has exactly one source, so a mixed selection cannot become one. */
    private selectedSourceKey = (): string | undefined => {
        const { selectedTools: selected } = this.state;

        if (!selected.length) {
            return undefined;
        }

        const first = toolLocationKey(selected[0]);

        if (!first || selected.some((t) => toolLocationKey(t) !== first)) {
            return undefined;
        }

        return first;
    };

    private handleGenerateTransfer = () => {
        const { selectedTools } = this.state;
        const fromKey = this.selectedSourceKey();

        if (!fromKey) {
            showError(translations.transfers.oneSourceWarehouseOnly);
            return;
        }

        this.setState({ transferPreset: { fromKey, toolIds: selectedTools.map((t) => t.id) } });
    };

    private handleTransferCreated = () => {
        this.setState({ transferPreset: undefined, selectedTools: [] });
        void this.getTools();
    };

    private renderLocation = (record: ToolVm): React.ReactElement => (
        <>
            {record.currentEmployeeId != null ? (
                <>
                    <UserOutlined style={{ marginRight: 6 }} />
                    {record.currentEmployeeName}
                </>
            ) : (
                record.currentWarehouseName
            )}
            {record.activeTransferId != null && (
                <Link
                    to={Routes.ROUTE_TRANSFERS_READ.replace(
                        ReplaceStrings.ENTITY_ID,
                        record.activeTransferId.toString()
                    )}
                >
                    <Tag color="blue" style={{ marginLeft: 8, cursor: 'pointer' }}>
                        {translations.transfers.inTransferTo} {record.activeTransferToName}
                    </Tag>
                </Link>
            )}
        </>
    );

    public render(): React.ReactElement {
        const {
            tools,
            warehouses,
            employees,
            loading,
            exporting,
            totalItems,
            selectedTools,
            ownProjects,
            transferPreset,
            allowCreateTransfer,
        } = this.state;
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const { pageIndexParam, pageSizeParam } = getPagingSortingParams(params);
        const { sortBy, sortOrder } = this.getSort();
        const filters = getFiltersParams(params);
        const { warehouseId, employeeId } = this.getLocationParams();
        const selectedToolIds = selectedTools.map((t) => t.id);
        const ownWarehouseIds = (warehouses || [])
            .filter(
                (w) =>
                    ownProjects == null ||
                    (w.projectId != null && ownProjects.includes(w.projectId.toString()))
            )
            .map((w) => w.id);
        const pagination: TablePaginationConfig = {
            ...defaultTablePagination,
            current:
                pageIndexParam && parseInt(pageIndexParam)
                    ? parseInt(pageIndexParam)
                    : defaultTablePagination.defaultCurrent,
            pageSize:
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
            total: totalItems,
        };

        const columns = [
            {
                title: translations.tools.number,
                dataIndex: 'number',
                width: 80,
                ...getSearchFilter(),
                filteredValue: filters.number ? [filters.number] : null,
                sorter: true,
                // Newest first; clearing the sort falls back to the same default.
                sortDirections: ['descend', 'ascend'] as SortOrder[],
                sortOrder: sortBy === 'number' ? sortOrder : null,
            },
            {
                title: translations.tools.image,
                dataIndex: 'thumbnailAttachmentId',
                width: LIST_IMAGE_PX + 24,
                render: (_: unknown, record: ToolVm): React.ReactElement =>
                    record.thumbnailAttachmentId ? (
                        <AuthenticatedAttachmentImage
                            attachmentId={record.thumbnailAttachmentId}
                            alt=""
                            width={LIST_IMAGE_PX}
                            height={LIST_IMAGE_PX}
                        />
                    ) : (
                        <ImagePlaceholder width={LIST_IMAGE_PX} height={LIST_IMAGE_PX} />
                    ),
            },
            {
                title: translations.projects.name,
                dataIndex: 'name',
                ...getSearchFilter(),
                sorter: true,
                filteredValue: filters.name ? [filters.name] : null,
                sortOrder: sortBy === 'name' ? sortOrder : null,
                render: (value: string, record: ToolVm): React.ReactElement => (
                    <Link
                        to={Routes.ROUTE_TOOLS_READ.replace(
                            ReplaceStrings.ENTITY_ID,
                            record.id.toString()
                        )}
                    >
                        {value}
                    </Link>
                ),
            },
            {
                title: translations.projects.description,
                dataIndex: 'description',
                ...getSearchFilter(),
                filteredValue: filters.description ? [filters.description] : null,
                sorter: true,
                sortOrder: sortBy === 'description' ? sortOrder : null,
            },
            {
                title: translations.tools.certificateValidUntil,
                dataIndex: 'certificateValidUntil',
                width: 130,
                sorter: true,
                sortOrder: sortBy === 'certificateValidUntil' ? sortOrder : null,
                render: (value?: Date): React.ReactElement => (
                    <CertificateValidity validUntil={value} />
                ),
            },
            {
                title: translations.transfers.location,
                key: 'location',
                render: (_: unknown, record: ToolVm): React.ReactElement =>
                    this.renderLocation(record),
            },
            {
                title: translations.general.actions,
                key: 'actions',
                width: 130,
                align: 'left',
                className: 'no-print',
                render: (value: ToolVm): React.ReactElement => {
                    const reason = this.notSelectableReason(value, ownWarehouseIds);

                    return (
                        <Space>
                            <Link
                                to={Routes.ROUTE_TOOLS_READ.replace(
                                    ReplaceStrings.ENTITY_ID,
                                    value.id.toString()
                                )}
                            >
                                {translations.general.details}
                            </Link>
                            {allowCreateTransfer && (
                                <Tooltip title={reason || translations.transfers.createTransfer}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<SwapOutlined />}
                                        disabled={!!reason}
                                        onClick={() =>
                                            this.setState({
                                                transferPreset: {
                                                    fromKey: toolLocationKey(value) as string,
                                                    toolIds: [value.id],
                                                },
                                            })
                                        }
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    );
                },
            },
        ];

        return (
            <>
                <Space style={{ marginBottom: 16 }} className="no-print">
                    <LocationSelect
                        allowClear
                        warehouses={warehouses || []}
                        employees={employees || []}
                        style={{ width: 260 }}
                        placeholder={translations.transfers.location}
                        value={locationKey(warehouseId, employeeId)}
                        onChange={this.handleLocationChange}
                    />

                    {allowCreateTransfer && (
                        <Button
                            type="primary"
                            icon={<SwapOutlined />}
                            disabled={!selectedToolIds.length}
                            onClick={this.handleGenerateTransfer}
                        >
                            {translations.transfers.generateTransfer}
                        </Button>
                    )}

                    <Dropdown
                        trigger={['click']}
                        disabled={exporting}
                        menu={{
                            items: [
                                {
                                    key: 'pdf',
                                    icon: <FilePdfOutlined />,
                                    label: translations.general.exportPdf,
                                    onClick: () => this.handleExport('pdf'),
                                },
                                {
                                    key: 'xlsx',
                                    icon: <FileExcelOutlined />,
                                    label: translations.general.exportXlsx,
                                    onClick: () => this.handleExport('xlsx'),
                                },
                            ],
                        }}
                    >
                        <Button loading={exporting}>
                            {translations.general.export} <DownOutlined />
                        </Button>
                    </Dropdown>
                </Space>

                <Table
                    locale={getTableLocale()}
                    columns={columns as any}
                    dataSource={tools}
                    loading={loading}
                    rowKey={(record: ToolVm): string => record.id?.toString()}
                    onChange={this.handleTableChange}
                    pagination={pagination}
                    rowSelection={
                        allowCreateTransfer
                            ? {
                                  preserveSelectedRowKeys: true,
                                  selectedRowKeys: selectedToolIds.map((id) => id.toString()),
                                  onChange: (_, rows) =>
                                      this.setState({
                                          selectedTools: rows.filter((r) => !!r),
                                      }),
                                  getCheckboxProps: (record: ToolVm) => {
                                      const reason = this.notSelectableReason(
                                          record,
                                          ownWarehouseIds
                                      );

                                      // The hover title tells the user why a row is greyed out.
                                      return { disabled: !!reason, title: reason };
                                  },
                              }
                            : undefined
                    }
                    bordered
                    size="small"
                />

                <Drawer
                    title={translations.transfers.addTransfer}
                    open={!!transferPreset}
                    onClose={() => this.setState({ transferPreset: undefined })}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    {transferPreset && (
                        <TransferForm
                            presetFromKey={transferPreset.fromKey}
                            presetToolIds={transferPreset.toolIds}
                            onClose={() => this.setState({ transferPreset: undefined })}
                            onSuccess={this.handleTransferCreated}
                        />
                    )}
                </Drawer>
            </>
        );
    }
}

export default ToolTable;
